import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { listarEquiposDeModelo, mensajeError } from "../services/catalogoEquiposService";

const cabecera = { fontWeight: 800, fontSize: 11.5, whiteSpace: "nowrap", bgcolor: "action.hover" };
const celda = { py: 0.5, px: 1, fontSize: 12.5 };

// Colores del estado del equipo para leerlo de un vistazo.
const colorEstado = (estado) => {
  if (["Activo", "Vendido Activo"].includes(estado)) return "success";
  if (["Inactivo", "Almacen", "Vendido", "Espera", "Transicion"].includes(estado)) return "info";
  if (["Mantenimiento"].includes(estado)) return "warning";
  if (["Baja", "Robado"].includes(estado)) return "error";
  return "default";
};

// Equipos registrados con un modelo (de todas las tablas de equipos). Paginado y con búsqueda en el servidor.
export default function EquiposDeModeloDialog({ modelo, onClose }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [buscar, setBuscar] = useState("");
  const [q, setQ] = useState("");
  const [pagina, setPagina] = useState(0);
  const [filas, setFilas] = useState(10);

  // Al abrir otro modelo se empieza de cero.
  useEffect(() => {
    setDatos(null); setBuscar(""); setQ(""); setPagina(0); setError("");
  }, [modelo?.id]);

  useEffect(() => {
    const t = setTimeout(() => { setQ(buscar.trim()); setPagina(0); }, 300);
    return () => clearTimeout(t);
  }, [buscar]);

  const cargar = useCallback(async () => {
    if (!modelo) return;
    setCargando(true);
    try {
      const { data } = await listarEquiposDeModelo(modelo.id, { page: pagina + 1, limit: filas, q: q || undefined });
      setDatos(data);
      setError("");
    } catch (e) {
      setError(mensajeError(e, "No se pudieron cargar los equipos."));
    } finally {
      setCargando(false);
    }
  }, [modelo, pagina, filas, q]);

  useEffect(() => { cargar(); }, [cargar]);

  const total = datos?.total ?? 0;

  return (
    <Dialog open={!!modelo} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography component="span" sx={{ fontSize: 17, fontWeight: 800 }}>Equipos con el modelo «{datos?.modelo?.etiqueta || modelo?.nombre}»</Typography>
          {datos && <Chip size="small" color={total ? "primary" : "default"} label={`${total} equipo${total === 1 ? "" : "s"}`} sx={{ fontWeight: 700 }} />}
        </Box>
        {datos?.modelo && (
          <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 400 }}>
            {datos.modelo.servicio} › {datos.modelo.tipo_equipo} › {datos.modelo.tipo_modelo}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5 }}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <TextField
            size="small" placeholder="Buscar por identificador, estado, ciudad o tipo…" value={buscar} onChange={(e) => setBuscar(e.target.value)} sx={{ width: { xs: "100%", sm: 360 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          />
          {cargando && <CircularProgress size={18} />}
        </Box>

        <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={cabecera}>Tipo</TableCell>
                <TableCell sx={cabecera}>Identificador</TableCell>
                <TableCell sx={cabecera}>Ref.</TableCell>
                <TableCell sx={cabecera}>Estado</TableCell>
                <TableCell sx={cabecera}>Ciudad</TableCell>
                <TableCell sx={cabecera}>Cliente</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!datos?.items?.length ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ ...celda, py: 3, textAlign: "center", color: "text.secondary" }}>
                    {cargando ? "Cargando…" : q ? "Ningún equipo coincide con la búsqueda." : "No hay equipos registrados con este modelo: se puede eliminar."}
                  </TableCell>
                </TableRow>
              ) : datos.items.map((e) => (
                <TableRow key={`${e.tipo}-${e.identificador}-${e.referencia}`} hover>
                  <TableCell sx={celda}>{e.tipo}</TableCell>
                  <TableCell sx={{ ...celda, fontWeight: 700 }}>{e.identificador}</TableCell>
                  <TableCell sx={celda}>{e.referencia}</TableCell>
                  <TableCell sx={celda}>{e.estado ? <Chip size="small" variant="outlined" color={colorEstado(e.estado)} label={e.estado} sx={{ height: 20, fontSize: 11 }} /> : "—"}</TableCell>
                  <TableCell sx={celda}>{e.ciudad}</TableCell>
                  <TableCell sx={celda}>{e.cliente || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={total} page={pagina} rowsPerPage={filas} rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => setPagina(p)} onRowsPerPageChange={(e) => { setFilas(Number(e.target.value)); setPagina(0); }}
          labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ textTransform: "none" }}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}
